import {
  Autocomplete,
  Backdrop,
  Box,
  CircularProgress,
  FormControl,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import HttpService from '../../hooks/Https-services';
import { API_ROUTES } from '../../utils/constant';
import moment from 'moment';
import { GlobalSearchContext } from '../../context/GlobalSearchContext';
import { DateRangePicker } from 'rsuite';
import 'rsuite/DateRangePicker/styles/index.css';

function TabPanel(props: any) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      sx={{ p: 3 }}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </Box>
  );
}

const UserIdleChart = () => {
  const [tableData, setTableData] = useState<any[]>([]);
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const { _searchQuery: sQuery, _searchQueryTag: sQueryTag }: any =
    React.useContext(GlobalSearchContext);
  const [userInfo, setUserInfo] = React.useState<any[]>([]);
  const [moderatorInfo, setModeratorInfo] = React.useState<any[]>([]);
  const [selectModrator, setSelectModerator] = React.useState<any>([]);
  const [selectAssign, setSelectAssign] = React.useState<any>([]);
  const [dateRangeModerator, setDateRangeModerator] = useState(null);
  const [dateRangeTagger, setDateRangeTagger] = useState(null);
  const [isFirstModeratorLoad, setIsFirstModeratorLoad] = React.useState(true);
  const [isFirstTaggerLoad, setIsFirstTaggerLoad] = React.useState(true);
  const handleDateChangeModerator = (value: any) => {
    setDateRangeModerator(value);
  };
  const handleDateChangeTagger = (value: any) => {
    setDateRangeTagger(value);
  };
  const {
    handleSearchChangeTag,
    debouncedQueryTag,
    handleSearchChange,
    debouncedQuery,
  }: any = useContext(GlobalSearchContext);
  const [optionsTagger, setOptionsTagger] = useState<
    { label: string; id: number }[]
  >([]);
  const [optionsMod, setOptionsMod] = useState<{ label: string; id: number }[]>(
    []
  );

  useEffect(() => {
    const optdata = userInfo?.map((el: any) => {
      return {
        label: el.name,
        id: el.id,
      };
    });
    setOptionsTagger(optdata);
  }, [userInfo]);
  useEffect(() => {
    const optdata = moderatorInfo?.map((el: any) => {
      return {
        label: el.name,
        id: el.id,
      };
    });
    setOptionsMod(optdata);
  }, [moderatorInfo]);
  const handleChangeModerator = (event: any, newValue: any) => {
    setSelectModerator(newValue);
  };

  const handleChangeAssign = (event: any, newValue: any) => {
    setSelectAssign(newValue);
  };

  const fetchUserModInfo = React.useCallback(async () => {
    try {
      // Filter users whose role includes "moderator"
      const resModerator: any = await HttpService.get(
        `${API_ROUTES.allUser}?roleId=${2}&search=${sQuery}`
      );

      setModeratorInfo(resModerator.data.data.list);

      if (isFirstModeratorLoad) {
        const defaultModerator = {
          id: resModerator.data.data.list[0].id,
          label: resModerator.data.data.list[0].name,
        };
        setSelectModerator(defaultModerator);
        setIsFirstModeratorLoad(false);
      }
    } catch (error: any) {
      console.error('Error fetching user info:', error);
    }
  }, [sQuery]);
  const fetchTaggerModInfo = React.useCallback(async () => {
    try {
      const resTagger: any = await HttpService.get(
        `${API_ROUTES.allUser}?roleId=${3}&search=${sQueryTag}`
      );

      setUserInfo(resTagger.data.data.list);
      if (isFirstTaggerLoad) {
        const defaultTagger = {
          id: resTagger.data.data.list[0].id,
          label: resTagger.data.data.list[0].name,
        };
        setSelectAssign(defaultTagger);
        setIsFirstTaggerLoad(false);
      }
    } catch (error: any) {
      console.error('Error fetching Tagger info:', error);
    }
  }, [sQueryTag]);

  React.useEffect(() => {
    fetchUserModInfo();
  }, [fetchUserModInfo]);
  React.useEffect(() => {
    fetchTaggerModInfo();
  }, [fetchTaggerModInfo]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const getReportData = useCallback(async () => {
    setLoading(true);
    const query: any = {};

    if (tabIndex === 0) {
      query.userId = selectModrator?.id;
      if (dateRangeModerator) {
        query.startDate = moment(dateRangeModerator[0]).format('DD/MM/YYYY');
        query.endDate = moment(dateRangeModerator[1]).format('DD/MM/YYYY');
      }
    } else {
      query.userId = selectAssign?.id;
      if (dateRangeTagger) {
        query.startDate = moment(dateRangeTagger[0]).format('DD/MM/YYYY');
        query.endDate = moment(dateRangeTagger[1]).format('DD/MM/YYYY');
      }
    }

    try {
      const response = await HttpService.get(
        `${API_ROUTES.idleReportGraph}?${new URLSearchParams(query).toString()}`
      );
      const apiData = response.data.data;
      setTableData(apiData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching chart data:', error);
      setLoading(false);
    }
  }, [
    tabIndex,
    selectModrator,
    selectAssign,
    dateRangeModerator,
    dateRangeTagger,
    HttpService,
    API_ROUTES,
  ]);

  useEffect(() => {
    getReportData();
  }, [getReportData]);

  const transformData = (data: Record<string, any>) => {
    // Extract keys (dates) and corresponding values
    const categories = Object.keys(data);

    // Map `totalDurationSum` from seconds to hours for seriesData
    const seriesData = categories.map((date) =>
      parseFloat(((data[date]?.totalDurationSum || 0) / 3600).toFixed(2))
    );

    return { categories, seriesData };
  };

  const { categories, seriesData } = transformData(tableData);

  const chartOptions: ApexCharts.ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: {
        show: false,
      },
      zoom: { enabled: false },
      width: '100%',
    },
    grid: {
      padding: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
    },
    xaxis: {
      categories: categories,
      title: {
        text: 'Date',
      },
      labels: {
        rotate: -45, // Rotate labels for better visibility
        style: {
          fontSize: '12px',
          fontFamily: 'Arial, sans-serif',
        },
      },
    },
    yaxis: {
      min: 0,
      // max: Math.max(...seriesData, 0) + 5,
      tickAmount: 5,
      labels: {
        formatter: (value: number) => `${value}`,
      },
      title: {
        text: 'Hours',
      },
    },
    stroke: {
      curve: 'smooth',
    },
    markers: {
      size: 5,
    },
    title: {
      text: 'Idle Data',
      align: 'center',
      style: {
        fontSize: '16px',
      },
    },
  };

  const series = [
    {
      name: 'Idle Hours',
      data: seriesData,
    },
  ];

  return (
    <>
      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        aria-label="Dashboard Tabs"
      >
        <Tab label="MODERATOR" />
        <Tab label="TAGGER" />
      </Tabs>
      <TabPanel
        value={tabIndex}
        index={0}
        sx={
          {
            // padding: '0px !important',
            // margin: '0px !important',
          }
        }
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            // justifyContent: 'space-between',
            gap: 2,
            alignItems: 'center',
            marginTop: 2,
          }}
        >
          <DateRangePicker
            character=" – "
            size="lg"
            onChange={handleDateChangeModerator}
          />
          <FormControl
            sx={{
              flexShrink: 0,
              width: { xs: 1, md: '170px' },
              margin: '0 !important',
              padding: '0 !important',
            }}
          >
            <Autocomplete
              size="small"
              id="disable-clearable"
              options={optionsMod}
              value={selectModrator}
              isOptionEqualToValue={(option, value) => option.id === value?.id} // Compare by id for selected item
              renderOption={(props: any, option: any) => {
                return (
                  <Box component="li" {...props} key={option?.id}>
                    <Typography
                      variant="body1"
                      sx={{ fontStyle: 'normal', textTransform: 'none' }}
                    >
                      {' '}
                      {option?.label}
                    </Typography>
                  </Box>
                );
              }}
              onClose={() => {
                const data = {
                  target: {
                    value: '',
                  },
                };
                handleSearchChange(data);
              }}
              onChange={(event, newValue: any) => {
                handleChangeModerator(event, newValue);
              }}
              renderInput={(params) => (
                <TextField
                  value={debouncedQuery}
                  onChange={(e) => handleSearchChange(e)}
                  {...params}
                  placeholder="Select Moderator"
                />
              )}
            />
          </FormControl>
        </Box>

        <Chart
          options={chartOptions}
          series={series}
          type="bar"
          height={350}
          width="100%"
        />
      </TabPanel>
      <TabPanel
        value={tabIndex}
        index={1}
        sx={
          {
            // padding: '0px !important',
            // margin: '0px !important',
          }
        }
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            // justifyContent: 'space-between',
            gap: 2,
            alignItems: 'center',
            marginTop: 2,
          }}
        >
          <DateRangePicker
            character=" – "
            size="lg"
            onChange={handleDateChangeTagger}
          />
          <FormControl
            sx={{
              flexShrink: 0,
              width: { xs: 1, md: '170px' },
              margin: '0 !important',
              padding: '0 !important',
            }}
          >
            <Autocomplete
              size="small"
              id="disable-clearable"
              isOptionEqualToValue={(option, value) => option.id === value?.id} // Compare by id for selected item
              onClose={() => {
                const data = {
                  target: {
                    value: '',
                  },
                };
                handleSearchChangeTag(data);
              }}
              value={selectAssign}
              options={optionsTagger}
              renderOption={(props: any, option: any) => {
                return (
                  <Box component="li" {...props} key={option?.id}>
                    <Typography
                      variant="body1"
                      sx={{ fontStyle: 'normal', textTransform: 'none' }}
                    >
                      {' '}
                      {option?.label}
                    </Typography>
                  </Box>
                );
              }}
              onChange={(event, newValue: any) => {
                handleChangeAssign(event, newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  value={debouncedQueryTag}
                  onChange={(e) => handleSearchChangeTag(e)}
                  placeholder="Select Assign"
                />
              )}
            />
          </FormControl>
        </Box>

        <Chart
          options={chartOptions}
          series={series}
          type="bar"
          height={350}
          width="100%"
        />
      </TabPanel>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};

export default UserIdleChart;
